import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'

/**
 * Runtime configuration interface.
 * It contains all the properties that are loaded from the config.json file and
 * that could change depending on the env where the application is deployed.
 */
export interface RuntimeConfig {
   environment: string
   apiUrl: string
   pmpIpUrl: string
   pmpIrUrl: string
}

/**
 * Service that provides the runtime configuration of the application.
 * It loads the configuration from a remote endpoint and provides it to the application.
 */
@Injectable({
   providedIn: 'root',
})
export class ConfigService {
   private readonly RUNTIME_CONFIG_PATH = 'assets/configs/properties.json'

   private config: RuntimeConfig = {
      environment: 'NONE',
      apiUrl: '',
      pmpIpUrl: '',
      pmpIrUrl: '',
   }

   constructor(private httpClient: HttpClient) {}

   /**
    * Initializes the dynamic configuration from the config.json file.
    * @returns {Promise<RuntimeConfig>} The dynamic configuration.
    */
   initRuntimeConfig(): Promise<RuntimeConfig> {
      return firstValueFrom(this.httpClient.get<RuntimeConfig>(`${this.RUNTIME_CONFIG_PATH}?ts=${Date.now()}`)).then((dc) => {
         if (!dc) {
            throw new Error('Runtime configuration not found')
         }
         this.config = dc
         console.log('Runtime Configuration loaded for env: ', this.environment)
         return dc
      })
   }

   /**
    * @returns {string} The current environment from the dynamic configuration. Expected values are NONE | LOCAL | DEV | TEST | PROD
    */
   get environment(): string {
      return this.config.environment
   }

   /**
    * Gets the API URL from the runtime configuration.
    *
    * @return {string} The API URL as specified in the runtime configuration.
    */
   get apiUrl(): string {
      return this.config.apiUrl + '/2.1'
   }

   get old_BE_ApiUrl(): string {
      return this.config.apiUrl + '/1.1'
   }

   get publicApiUrl(): string {
      return this.config.apiUrl + '/1.0'
   }

   /**
    * Retrieves the PMP IP URL from the configuration.
    * @return {string} The PMP IP URL.
    */
   get pmpIpUrl(): string {
      return this.config.pmpIpUrl
   }

   /**
    * Retrieves the PMP IR URL from the configuration.
    * @return {string} The PMP IR URL.
    */
   get pmpIrUrl(): string {
      return this.config.pmpIrUrl
   }
}
